import type {
    BrandId,
    CoverageRange,
    EquipmentTypeFamily,
    EquipmentTypeId,
    Model,
    ModelDocumentId,
    StatedModelRange,
} from '@rock/types';
import {
    MIN_COVERAGE_PREFIX_LENGTH,
    coverageAppliesToModel,
    coverageRangeFromEndpoints,
    coverageRangeKey,
    formatCoverageRange,
    normalizeCoveragePrefix,
} from '@rock/manuals';

// The publish form's coverage state, as pure data. Kept out of `CoverageFields.tsx` so the rules that
// decide what a reviewer is shown — especially which predicates arrive pre-filled — are testable without
// standing up a DAO context (the component file constructs a ModelDao at import).

/** The publish form's in-progress coverage rule. No prefixes AND no ranges = no rule; that IS the off switch. */
export interface CoverageDraft {
    prefixes: string[];
    ranges: CoverageRange[];
}

/** What the document is for, as the form holds it: the brands and types its rule (and its links) are read against. */
export interface DocumentScope {
    brandIds: BrandId[];
    typeIds: EquipmentTypeId[];
}

/**
 * What a publish says about the document: what it is for, the rule the reviewer confirmed (absent = this
 * model only), and whether the model under review is linked at all. Unlinking is only ever reachable WITH
 * a rule — without one, nothing else would carry the document to any model.
 */
export interface PublishScope {
    scope: DocumentScope;
    rule: CoverageDraft | undefined;
    linkThisModel: boolean;
    /** The document these bytes were offered to replace: publishing marks it replaced by them. */
    replaces?: ModelDocumentId;
}

/** Whether the draft carries any predicate — the "publish with coverage" switch. */
export function coverageDraftActive(draft: CoverageDraft): boolean {
    return draft.prefixes.length + draft.ranges.length > 0;
}

/**
 * Whether an ACTIVE draft, with the scope on screen, would reach one model — the same tiered test the
 * server applies (`coverageAppliesToModel`). An inactive draft covers nothing, so it answers false.
 *
 * This is what makes the publish form able to notice that the reviewer typed a rule EXCLUDING the model
 * they're reviewing — the case where linking it here is a mistake rather than the point.
 */
export function coverageDraftCoversModel(
    draft: CoverageDraft,
    scope: DocumentScope,
    model: Pick<Model, 'brandId' | 'typeId' | 'modelCanonical'>,
    families: readonly EquipmentTypeFamily[],
): boolean {
    if (!coverageDraftActive(draft)) return false;
    return !!coverageAppliesToModel({ ...scope, coverage: { ...draft, confirmedAt: '' } }, model, families);
}

/** The union of several scopes, in order, deduped — a re-publish starts from the document's plus the model's. */
export function mergeScopes(...scopes: (DocumentScope | undefined)[]): DocumentScope {
    return {
        brandIds: [...new Set(scopes.flatMap((scope) => scope?.brandIds ?? []))],
        typeIds: [...new Set(scopes.flatMap((scope) => scope?.typeIds ?? []))],
    };
}

/**
 * Why a scope can't be saved yet, or undefined when it can. Callers toast this and abort: every document
 * names at least one brand and one type (`assertValidDocumentScope` refuses the write anyway).
 */
export function documentScopeError(scope: DocumentScope): string | undefined {
    if (scope.brandIds.length === 0) return 'Add the brand this document is for';
    if (scope.typeIds.length === 0) return 'Add the equipment type this document is for';
    return undefined;
}

/**
 * Why a NORMALIZED prefix can't join this draft, or undefined. Deliberately silent about a prefix that
 * is merely too short: the entry row shows this verdict live as the reviewer types, and every prefix is
 * too short on its way to being long enough. Length is enforced at add time instead.
 */
export function checkCoveragePrefix(draft: CoverageDraft, prefix: string): string | undefined {
    if (draft.ranges.some((range) => range.prefix === prefix)) return `A range already uses ${prefix} as its stem`;
    return undefined;
}

/**
 * Why a derived range can't join this draft, or undefined. The inverse of {@link checkCoveragePrefix}'s
 * stem clash: a bare prefix already covers everything a range under the same stem would narrow.
 */
export function checkCoverageRange(draft: CoverageDraft, range: CoverageRange): string | undefined {
    return draft.prefixes.includes(range.prefix)
        ? `Prefix ${range.prefix} already covers ${formatCoverageRange(range)}`
        : undefined;
}

/** A fresh draft: no coverage. */
export function emptyCoverageDraft(): CoverageDraft {
    return { prefixes: [], ranges: [] };
}

/**
 * One offered predicate, carrying its KIND as data — never a string another layer has to re-interpret.
 * `label` is both the chip text and the identity used to tell offered-from-taken apart.
 */
export type CoverageSuggestion =
    | { kind: 'prefix'; label: string; prefix: string }
    | { kind: 'range'; label: string; range: CoverageRange };

/** A suggestion's IDENTITY (never its label — see `coverageRangeKey`), for offered-vs-taken and dedupe. */
export function suggestionKey(suggestion: CoverageSuggestion): string {
    return suggestion.kind === 'range' ? coverageRangeKey(suggestion.range) : suggestion.prefix;
}

/** What the verifier extracted about one candidate: model numbers (text) and stated ranges (structured). */
export interface CoverageExtraction {
    coversModels?: string[];
    coversRanges?: StatedModelRange[];
}

/**
 * A candidate's starting draft: the verifier's extraction already IN the list. Pre-filling is safe
 * because the form is the confirmation step — the predicates, the document's brands and types, and the
 * "matches N existing models" preview all sit in front of the reviewer, above the Publish button, and
 * removing a chip is one click.
 */
export function coverageDraftFromExtraction(extraction: CoverageExtraction | undefined): CoverageDraft {
    const draft = emptyCoverageDraft();
    for (const suggestion of coverageSuggestions(extraction)) {
        if (suggestion.kind === 'range') draft.ranges.push(suggestion.range);
        else draft.prefixes.push(suggestion.prefix);
    }
    // A prefix equal to a range's stem is an invalid combo (the write-time assert refuses it) — when the
    // extraction yields both, the range is the more precise claim, so it wins the seed.
    draft.prefixes = draft.prefixes.filter((prefix) => !draft.ranges.some((range) => range.prefix === prefix));
    return draft;
}

/**
 * The verifier's extraction as offerable predicates. Ranges arrive as VERBATIM endpoint pairs and go
 * through the same derivation typed endpoints do (`coverageRangeFromEndpoints`) — one code path, so an
 * extracted range and a hand-entered one can't mean different things; an underivable pair is dropped
 * rather than surfaced as a chip that means nothing. `coversModels` are model numbers, so they become
 * prefixes whole — a prefix of the full number still reaches its suffixed variants (`1060` → `1060X`).
 * The verifier also writes each stated range into `coversModels` as text ("KSM45–KSM200 Series"); folded,
 * that is a prefix no model starts with, so a range's text twin is not offered.
 */
export function coverageSuggestions(extraction: CoverageExtraction | undefined): CoverageSuggestion[] {
    const byKey = new Map<string, CoverageSuggestion>();
    const offer = (suggestion: CoverageSuggestion) => {
        const key = suggestionKey(suggestion);
        if (!byKey.has(key)) byKey.set(key, suggestion);
    };
    const stated = extraction?.coversRanges ?? [];
    for (const { from, to } of stated) {
        const derived = coverageRangeFromEndpoints(from, to);
        if (!derived.ok) continue;
        offer({ kind: 'range', label: formatCoverageRange(derived.range), range: derived.range });
    }
    const rangeTexts = stated.map(({ from, to }) => normalizeCoveragePrefix(`${from}${to}`));
    for (const raw of extraction?.coversModels ?? []) {
        const prefix = normalizeCoveragePrefix(raw);
        if (prefix.length < MIN_COVERAGE_PREFIX_LENGTH) continue;
        if (rangeTexts.some((text) => prefix.startsWith(text))) continue;
        offer({ kind: 'prefix', label: prefix, prefix });
    }
    return [...byKey.values()];
}
